// src/context/AuthContext.tsx

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// 1. Definir la interfaz para el estado de autenticación y las acciones
interface AuthContextType {
  isAuthenticated: boolean;
  user: { username: string } | null;
  login: (token: string, userData: { username: string }) => void;
  logout: () => void;
}

// 2. Crear el contexto con un valor por defecto
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// 3. Crear el proveedor del contexto (Provider)
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<{ username: string } | null>(null);

  // Base URL configurable via .env (Añade esta línea si aún no la tienes)
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, '') || 'http://localhost:3001';

  // Cargar estado desde localStorage al inicio
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const userData = localStorage.getItem('user_data');
    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setIsAuthenticated(true);
        setUser(parsedUser);
      } catch (e) {
        // Limpiar en caso de que los datos de localStorage estén corruptos
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
      }
    }
  }, []);

  const login = (token: string, userData: { username: string }) => {
    localStorage.setItem('auth_token', token);
    localStorage.setItem('user_data', JSON.stringify(userData));
    setIsAuthenticated(true);
    setUser(userData);
  };

  const logout = async () => {
    try {
      // 1. Obtener el token del localStorage
      const token = localStorage.getItem('auth_token');

      // 2. Enviar la solicitud POST al servidor para invalidar el token
      if (token) {
        const resp = await fetch(`${API_BASE_URL}/api/autenticar/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}` // Adjuntar el token en el encabezado de autorización
          },
        });

        if (!resp.ok) {
          // Manejar posibles errores del servidor, aunque borremos el token localmente
          console.error('Error al cerrar sesión en el servidor:', await resp.json());
        }
      }
    } catch (error) {
      console.error('Error de red al intentar cerrar sesión:', error);
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  const value = {
    isAuthenticated,
    user,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// 4. Crear un hook personalizado para facilitar el uso del contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
}
